import styled from "@emotion/styled";

import { MDSIconKind, MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds";
import { AgentLinkButton } from "@/pages/agent/components/agent-link-button";
import { AgentOpenTasksBadge } from "@/pages/agent/components/agent-open-tasks-badge";
import { AgentRowActionButton } from "@/pages/agent/components/agent-row-action-button";
import { AgentTaskStateIcon } from "@/pages/agent/components/agent-task-state-icon";
import {
  ProjectSectionKind,
  type ProjectCardViewModel,
  type ProjectNextUp,
} from "@/pages/agent/projects/agent-projects-page.utils";

export const AgentProjectCard = ({
  kind,
  projectCard,
  onOpenActivities,
  onOpenProject,
  onOpenTasks,
  onOpenNextUp,
  onCommentProject,
}: {
  kind: ProjectSectionKind;
  projectCard: ProjectCardViewModel;
  onOpenActivities: () => void;
  onOpenProject: ({ projectId }: { projectId: string }) => void;
  onOpenTasks: () => void;
  onOpenNextUp: ({ projectId }: { projectId: string }) => void;
  onCommentProject: ({ projectId }: { projectId: string }) => void;
}) => {
  const projectTitle = projectCard.project.title || "Untitled project";
  const nextUpText = formatNextUpText({ nextUp: projectCard.nextUp });
  const hasNextUp = projectCard.nextUp !== null;

  return (
    <StyledProjectCard>
      <StyledCardHeader>
        <StyledTitleRow>
          {kind === ProjectSectionKind.Completed && <AgentTaskStateIcon completed size={14} />}
          <StyledProjectTitleButton
            type="button"
            onClick={() => {
              onOpenProject({ projectId: projectCard.project.id });
            }}
          >
            <MDSText
              color={MDSTextColor.Primary}
              variant={MDSTextVariant.TextM}
              weight={MDSTextWeight.SemiBold}
            >
              {projectTitle}
            </MDSText>
          </StyledProjectTitleButton>
          {projectCard.openTasksCount > 0 ? (
            <AgentOpenTasksBadge openTasksCount={projectCard.openTasksCount} />
          ) : null}
        </StyledTitleRow>

        <StyledCardActions>
          <AgentRowActionButton
            aria-label={`Open tasks for ${projectTitle}`}
            iconKind={MDSIconKind.ListCheck}
            iconSize={14}
            size={24}
            onClick={onOpenTasks}
          />
          <AgentRowActionButton
            aria-label={`Open activity for ${projectTitle}`}
            iconKind={MDSIconKind.History}
            iconSize={14}
            size={24}
            onClick={onOpenActivities}
          />
          <AgentRowActionButton
            aria-label={`Comment on ${projectTitle}`}
            iconKind={MDSIconKind.Message}
            iconSize={14}
            size={24}
            onClick={() => {
              onCommentProject({ projectId: projectCard.project.id });
            }}
          />
        </StyledCardActions>
      </StyledCardHeader>

      <StyledDivider />

      <StyledDescription color={MDSTextColor.Secondary} variant={MDSTextVariant.ParagraphS}>
        {projectCard.project.description || "No description yet."}
      </StyledDescription>

      <StyledNextUpLine>
        <MDSText
          color={MDSTextColor.Primary}
          variant={MDSTextVariant.TextS}
          weight={MDSTextWeight.SemiBold}
        >
          Next up:
        </MDSText>
        {hasNextUp ? (
          <AgentLinkButton
            onClick={() => {
              onOpenNextUp({ projectId: projectCard.project.id });
            }}
          >
            {nextUpText}
          </AgentLinkButton>
        ) : (
          <MDSText color={MDSTextColor.Tertiary} variant={MDSTextVariant.TextS}>
            {nextUpText}
          </MDSText>
        )}
      </StyledNextUpLine>
    </StyledProjectCard>
  );
};

const formatNextUpText = ({ nextUp }: { nextUp: ProjectNextUp | null }) => {
  if (!nextUp) {
    return "—";
  }

  if (nextUp.detail === "—") {
    return nextUp.label;
  }

  return `${nextUp.label}, ${nextUp.detail}`;
};

const StyledProjectCard = styled.article`
  background: ${({ theme }) => theme.colors.background.input.chrome.default};
  border: 1px solid ${({ theme }) => theme.colors.border.surface};
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing[3]};
  min-height: 176px;
  min-width: 0;
  padding: ${({ theme }) => theme.spacing[5]};

  &:hover {
    background: ${({ theme }) => theme.colors.background.input.chrome.hover};
  }
`;

const StyledCardHeader = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[3]};
  justify-content: space-between;
  min-width: 0;
`;

const StyledTitleRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.spacing[2]};
  min-width: 0;
`;

const StyledProjectTitleButton = styled.button`
  background: transparent;
  border: 0;
  cursor: pointer;
  min-width: 0;
  padding: 0;
  text-align: left;

  span {
    overflow-wrap: anywhere;
  }

  &:hover span {
    color: ${({ theme }) => theme.colors.text.emphasis};
  }
`;

const StyledCardActions = styled.div`
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: ${({ theme }) => theme.spacing[1]};
`;

const StyledDivider = styled.div`
  background: ${({ theme }) => theme.colors.border.surface};
  height: 1px;
  width: 100%;
`;

const StyledDescription = styled(MDSText)`
  overflow-wrap: anywhere;
`;

const StyledNextUpLine = styled.div`
  align-items: baseline;
  display: flex;
  gap: ${({ theme }) => theme.spacing[1]};
  min-width: 0;
`;
